// src/app/settings/settings.component.ts
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  inject,
  Input,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { IonIcon, IonButton, IonSpinner, ModalController } from '@ionic/angular/standalone';
import { CommonModule, Location } from '@angular/common';
import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs'; // For awaiting observables
import { settingsConfig, SettingConfigTab, SettingConfigItem } from '@app/configs/settings-config';
import { ApiService } from '@app/services/api.service';
import { ToasterService } from '@app/services/toaster.service';
import { ToastMessages } from '@app/constants/toast-messages.constants';
import { AppService } from '@app/services/app.service';
import { SettingsSavedService } from './settings-saved.service';
import { SetupPreferenceComponent } from '@app/components/setup-preference/setup-preference.component';
import { NbsSelectDropdownComponent } from '../nbs-select-dropdown/nbs-select-dropdown.component';
import { select, Store } from '@ngrx/store';
import { AppState } from '@app/types/app.types';
import { RouteAccessService } from '@app/services/route-access.service';

interface FilterPayload {
  filterKey: string;
  values: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
  standalone: true,
  imports: [CommonModule, IonIcon, IonButton, IonSpinner, NbsSelectDropdownComponent, SetupPreferenceComponent],
})
export class SettingsComponent implements OnInit, AfterViewInit {
  @Output() openSettings = new EventEmitter<boolean>();
  @Output() settingsSaved = new EventEmitter<void>();
  @Input() initialTab: string | undefined;
  @Input() role!: string;
  @Input() bottlerId!: string;

  @ViewChild('tabContentHost', { read: ViewContainerRef })
  tabContentHost!: ViewContainerRef;

  settingsConfig: SettingConfigTab[] = settingsConfig;
  selectedTab: string = '';
  selectedTabIndex: number = -1;
  loading = false;
  isInitialAPILoaded = true;
  adminSettingData: any[] = []; // Consider a more specific type if possible
  settingSavedStatus: string = 'failed';
  payload: { filters: FilterPayload[] } = { filters: [] };
  private lastSavedFilters: { [key: string]: string } = {};
  public hasUnsavedChanges: boolean = false;
  pathFromLocation: string = '';

  isSettingBeingSaved: boolean = false;
  // Property to hold the currently selected tab's config for direct rendering in HTML
  currentTabConfig: SettingConfigTab | undefined;

  private appService = inject(AppService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();
  screenAccess$!: Observable<string[]>;
  private userScreenAccess: string[] = [];

  constructor(
    private apiService: ApiService,
    private toastService: ToasterService,
    private modalController: ModalController,
    private settingSavedService: SettingsSavedService,
    private store: Store<AppState>, // <--- Inject NGRX Store
    private routeAccessService: RouteAccessService,
    private location: Location,
  ) {}

  async ngOnInit(): Promise<void> {
    this.screenAccess$ = this.store.pipe(select((state: any) => state.app.screenAccessState));
    this.screenAccess$.pipe(takeUntil(this.destroy$)).subscribe((access) => {
      this.userScreenAccess = access;
      // Re-evaluate tab visibility and selection if screen access changes
      this.initializeTabSelection();
    });

    await this.getSettingsFilterOptions();
  }

  ngAfterViewInit(): void {
    // Now that ViewChild is guaranteed to be initialized, load the content.
    // This ensures `this.tabContentHost` is available.
    if (this.selectedTabIndex !== -1) {
      this.loadTabContent(this.selectedTabIndex);
      // Manually trigger change detection to render the dynamically loaded component immediately.
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeTabSelection(): void {
    let tabToSelect: SettingConfigTab | undefined;
    // console.log('Role in setting component is ', this.role);
    // console.log('Bottler id in settings component is ', this.bottlerId);

    if (this.initialTab) {
      tabToSelect = this.settingsConfig.find((tab) => tab.key === this.initialTab && this.isTabVisible(tab));
    }

    // If initialTab was not provided, or not found/visible, select the first visible tab
    if (!tabToSelect) {
      tabToSelect = this.settingsConfig.find((tab) => this.isTabVisible(tab));
    }

    if (tabToSelect) {
      const tabIndex = this.settingsConfig.indexOf(tabToSelect);
      // Only update if the selected tab is actually changing
      if (this.selectedTab !== tabToSelect.key) {
        this.selectedTab = tabToSelect.key;
        this.selectedTabIndex = tabIndex;
        this.currentTabConfig = tabToSelect;
        // Load content if it's a new tab selection
        this.loadTabContent(tabIndex);
        this.changeDetectorRef.detectChanges(); // Ensure view updates
      }
    } else {
      console.warn('No settings tabs are visible for the current user or initialTab is invalid. Closing settings.');
      if (!this.selectedTab || !this.isTabVisible(this.currentTabConfig!)) {
        this.cancelButton(); // Emit close event if no tab can be displayed
      }
      // this.cancelButton(); // Emit close event if no tab can be displayed
    }
  }

  isTabVisible(tab: SettingConfigTab): boolean {
    // Check bottlerId visibility
    const bottlerIdMatches = !tab.bottlerIds || tab.bottlerIds.includes(this.bottlerId);
    // Consolidation Changes:  the below line was added so that the tab is visible for the bottler ADMIN role even if the bottlerId doesn't match.
    // const roleMatches = !tab.role || tab.role === this.role;

    // Check screenAccess visibility using the shared service
    const screenAccessMatches = this.routeAccessService.hasAccessToRoute(
      this.userScreenAccess,
      tab.screenAccess || [], // Pass an empty array if screenAccess is undefined
    );

    // Consolidation Changes
    // return bottlerIdMatches && screenAccessMatches;
    // return (bottlerIdMatches || roleMatches) && screenAccessMatches;
    return bottlerIdMatches && screenAccessMatches;
  }

  cancelButton(): void {
    this.openSettings.emit(false);
    if (this.settingSavedStatus === 'success') {
      this.settingSavedService.triggerComponentReinitialize();
    }
    // Uncomment if this component is always used within a modal and should dismiss itself
    // this.modalController.dismiss();
  }

  selectTab(tabKey: string, index: number): void {
    this.selectedTab = tabKey;
    this.selectedTabIndex = index;
    this.currentTabConfig = this.settingsConfig[index]; // Update currentTabConfig for HTML rendering
    this.payload.filters = []; // Reset payload when tab changes (as per original logic)

    this.loadTabContent(index); // Load the content for the new tab
    this.changeDetectorRef.detectChanges(); // Trigger change detection after tab switch
  }

  loadTabContent(index: number): void {
    // This guard is now less likely to be hit for initial load, but good to keep.
    if (!this.tabContentHost) {
      console.warn('tabContentHost is not available yet.');
      return;
    }

    this.tabContentHost.clear(); // Clear any previously loaded dynamic component

    const tab = this.settingsConfig[index];

    if (tab && tab.component) {
      // If the tab has a dedicated component, load it dynamically
      const componentRef = this.tabContentHost.createComponent(tab.component);
    }
  }

  async getSettingsFilterOptions(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.getAdminSettingsBottlerRoleInfo());
      this.adminSettingData = response.data.map((filter: { default_options: any[]; selected: any }) => {
        const selectedOption = filter.default_options.find((opt) => opt.value === filter.selected?.value);
        filter.selected = selectedOption || filter.default_options[0];
        return filter;
      });

      this.lastSavedFilters = {};
      this.adminSettingData.forEach((filter) => {
        if (filter.filter_key && filter.selected?.value) {
          this.lastSavedFilters[filter.filter_key] = filter.selected.value;
        }
      });
      this.hasUnsavedChanges = false;
      Promise.resolve().then(() => {
        this.isInitialAPILoaded = false;
        this.loadTabContent(this.selectedTabIndex);
        // No need to call loadTabContent here again, it's called in ngAfterViewInit
        this.changeDetectorRef.detectChanges(); // Ensure view updates after data and component load
      });
    } catch (error: any) {
      this.toastService.error({
        message: 'Failed to load settings options',
        payload: { message: error?.message },
      });
      Promise.resolve().then(() => {
        this.isInitialAPILoaded = false; // Ensure loading state is updated even on error
        this.changeDetectorRef.detectChanges(); // Ensure view updates
      });
    }
  }

  isButtonDisabled(): boolean {
    return this.loading || !this.hasUnsavedChanges;
  }

  // This method handles changes from settings that use the traditional app-nbs-select
  onSettingsChange(key: string, selectedValue: any): void {
    // Assuming selectedValue is an array from nbs-select, and we want the first item
    const valueToStore = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;

    // Find the filter in adminSettingData and update its selected value
    const adminFilter = this.adminSettingData.find((d) => d.filter_key === key);
    if (adminFilter) {
      adminFilter.selected = { value: valueToStore, label: valueToStore }; // Assuming label can be same as value for simplicity
    }
    this.hasUnsavedChanges = this.checkIfUserSettingChangesExist();
  }

  private checkIfUserSettingChangesExist(): boolean {
    for (const filter of this.adminSettingData) {
      const currentFilterValue = filter.selected?.value;
      const lastSavedFilterValue = this.lastSavedFilters[filter.filter_key];

      if (currentFilterValue !== lastSavedFilterValue) {
        return true;
      }
    }
    return false;
  }

  // This method is for settings that use the traditional app-nbs-select
  isSettingVisible(setting: SettingConfigItem): boolean {
    // If roles are not defined for the setting, it's considered a match.
    // Otherwise, check if the current role is included.
    const roleMatches = !setting.roles || setting.roles.includes(this.role);

    // If bottlerIds are not defined for the setting, it's considered a match.
    // Otherwise, check if the current bottlerId is included.
    const bottlerIdMatches = !setting.bottlerIds || setting.bottlerIds.includes(this.bottlerId);

    return roleMatches && bottlerIdMatches;
  }

  getSelectedValue(filterKey: string) {
    const filter = this.adminSettingData.find((d) => d.filter_key === filterKey);
    return filter?.selected?.value ?? null;
  }

  getUserSettingOptions(filterKey: string) {
    const filter = this.adminSettingData.find((d) => d.filter_key === filterKey);
    return filter?.default_options;
  }

  async saveSettings() {
    this.pathFromLocation = this.location.path();
    console.log('Path from Location:', this.pathFromLocation);
    this.loading = true;
    this.payload.filters = this.adminSettingData
      .filter((filter) => {
        const currentFilterValue = filter.selected?.value;
        const lastSavedFilterValue = this.lastSavedFilters[filter.filter_key];
        return currentFilterValue !== lastSavedFilterValue;
      })
      .map((filter) => ({
        filterKey: filter.filter_key,
        values: filter.selected.value,
      }));

    if (this.payload.filters.length === 0) {
      this.loading = false;
      this.toastService.info({ message: 'No changes to save.' });
      this.hasUnsavedChanges = false;
      return;
    }
    try {
      await firstValueFrom(this.apiService.setAdminSettingsBottlerRole(this.payload));
      this.settingsSaved.emit();
      await this.appService.fetchApplicationData(this.payload.filters, this.pathFromLocation);
      this.adminSettingData.forEach((filter) => {
        this.lastSavedFilters[filter.filter_key] = filter.selected.value;
      });
      this.payload.filters = [];
      this.hasUnsavedChanges = false;
      this.loading = false;
      this.settingSavedStatus = 'success';

      this.toastService.success({ message: ToastMessages.settings.success });
      this.modalController.dismiss();
    } catch (error: any) {
      this.loading = false;
      this.settingSavedStatus = 'failed';
      this.toastService.error({
        message: ToastMessages.settings.error,
        payload: { message: error?.message },
      });
    }
  }

  isTabSwitchDisabled(targetTabKey: string): boolean {
    const isCurrentlyUserSettings = this.currentTabConfig?.key !== 'user-preference';

    if (isCurrentlyUserSettings && this.loading && targetTabKey === 'user-preference') {
      this.isSettingBeingSaved = true;
      return true; // Disable switching to Rec Preference if User Settings save is active
    }

    // For all other scenarios, tab switching is allowed
    this.isSettingBeingSaved = false;
    return false;
  }
}
